import React from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import Autosuggest from 'react-autosuggest'
import qs from 'qs'
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'
import find from 'lodash/find'
import Icon from '../misc/Icons'
import SearchProviderContainer from '../../containers/search/SearchProviderContainer'
import { useSearchContext } from '../../services/search/SearchContext'
import { useSupplementarySearch } from '../../services/search/SupplementarySearchContext'
import { getHitKind } from '../../utils/search/getHitKind'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getRepoforId } from '../../utils/getRepoforId'
import { isCurrentUser } from '../../utils/isCurrentUser'
import { setLastLocation } from '../routing/LastLocationProvider'
import { getLinkForRepo } from '../../utils/getLinkForRepo'
import { newSet, newQuestion } from '../../utils/newItem'
import { showNewRepoModal, showNewSectionModal } from '../../actions/modals'
import { STATIC_QUICK_ACTIONS, toQuickActionHit } from '../../services/search/local/quickActionsRegistry'
import { buildHelpSearchUrl } from '../../services/search/local/externalLinksRegistry'
import SearchPreviewPane from './SearchPreviewPane'

// Map a section index name to the SVG icon to use in the section header.
function getSectionIcon(index) {
  if (index === 'Quick Actions') return 'compose'
  if (index === 'Recent') return 'history'
  if (index === 'Navigation') return 'compass'
  if (index === 'Help & External') return 'book-open'
  if (
    index === 'repos' || index === 'folders' || index === 'sets_questions' ||
    index === 'Packs (Local)' || index === 'Folders (Local)' || index === 'Items (Local)'
  ) return 'library'
  return 'library'
}

// Human-readable label for a section header.
function getSectionLabel(index) {
  const labels = {
    repos: 'Library',
    folders: 'Library',
    // eslint-disable-next-line camelcase
    sets_questions: 'Library',
    'Packs (Local)': 'Library',
    'Folders (Local)': 'Library',
    'Items (Local)': 'Library',
    'Quick Actions': 'Quick Actions',
    Recent: 'Recent',
    Navigation: 'Navigation',
    'Help & External': 'Help',
  }
  return labels[index] || index
}

// Library-type section indexes. The first one encountered gets the "Library"
// header; subsequent ones use a blank header (no label, no border flicker).
const LIBRARY_INDEXES = new Set([
  'repos', 'folders', 'sets_questions',
  'Packs (Local)', 'Folders (Local)', 'Items (Local)',
])

function renderSectionTitle(section) {
  if (!section.index) return null
  // `showHeader` is pre-computed in buildAllSections (see below) so
  // renderSectionTitle never has to carry mutable state between calls.
  if (!section.showHeader) return null
  const label = getSectionLabel(section.index)
  const icon = getSectionIcon(section.index)
  return (
    <div className='searchDropdown-sectionHeader'>
      <div className='searchDropdown-sectionHeader-icon'>
        <Icon name={icon} />
      </div>
      <span className='searchDropdown-sectionHeader-label'>{label}</span>
    </div>
  )
}

// ─── Recent helpers ────────────────────────────────────────────────────────────
// Resolve `state.recentActivities` (sorted by activityTime desc) into hit-shaped
// rows by joining against `state.sets` / `state.questions`. Items that haven't
// hydrated yet are skipped (they'll appear once the data loads).
function buildRecentSuggestions(recentActivities, sets, questions, limit = 5) {
  if (!Array.isArray(recentActivities) || recentActivities.length === 0) return []
  const ordered = sortBy(recentActivities, 'activityTime').reverse()
  const hits = []
  for (let i = 0; i < ordered.length && hits.length < limit; i += 1) {
    const activity = ordered[i]
    if (activity && activity.item) {
      let item = null
      if (activity.itemType === 'set') {
        item = find(sets, { id: activity.item })
      } else if (activity.itemType === 'question') {
        item = find(questions, { id: activity.item })
      }
      if (item) {
        hits.push({
          id: `recent-${activity.itemType}-${item.id}`,
          objectID: `recent-${activity.itemType}-${item.id}`,
          __recent: true,
          __recentItemType: activity.itemType,
          item,
        })
      }
    }
  }
  return hits
}

// Empty-state sections — shown when the input is focused with no query.
// Pure function of static config + Redux-derived recent items.
function buildEmptyStateSections(recentSuggestions) {
  const sections = []
  sections.push({
    index: 'Quick Actions',
    hits: STATIC_QUICK_ACTIONS.map(toQuickActionHit),
    showHeader: true,
  })
  if (recentSuggestions.length > 0) {
    sections.push({
      index: 'Recent',
      hits: recentSuggestions,
      showHeader: true,
    })
  }
  return sections
}

// Merge Algolia sections + supplementary sections into a flat list, ordering:
//   1. Quick Actions  2. Navigation  3. Help & External  4. Library
// Empty sections are stripped first (prevents orphan dividers).
// Library-type sections get showHeader:true only on the first occurrence.
function buildAllSections(suggestions, additiveResults, currentRefinement) {
  const additive = Array.isArray(additiveResults) ? additiveResults : []

  // ── Quick Actions (shown first when present) ───────────────────────────────
  const head = []
  const qaBlock = additive.find((r) => r.providerId === 'quick-actions')
  if (qaBlock && Array.isArray(qaBlock.hits) && qaBlock.hits.length > 0) {
    head.push({ index: 'Quick Actions', hits: qaBlock.hits })
  }

  // ── Navigation + External ──────────────────────────────────────────────────
  const navBlock = additive.find((r) => r.providerId === 'navigation')
  if (navBlock && Array.isArray(navBlock.hits) && navBlock.hits.length > 0) {
    head.push({ index: 'Navigation', hits: navBlock.hits })
  }
  const extBlock = additive.find((r) => r.providerId === 'external-help')
  if (extBlock && Array.isArray(extBlock.hits) && extBlock.hits.length > 0) {
    const extHits = extBlock.hits.slice()
    // Synthetic trailing row that deep-links to the Plickers help center search.
    extHits.push({
      __helpShowMore: true,
      query: currentRefinement,
      objectID: 'help-show-more',
    })
    head.push({ index: 'Help & External', hits: extHits })
  } else if (currentRefinement) {
    // Even without help hits, still offer the deep link as its own one-row section.
    head.push({
      index: 'Help & External',
      hits: [{
        __helpShowMore: true,
        query: currentRefinement,
        objectID: 'help-show-more',
      }],
    })
  }

  // ── Library sections: Algolia first, then local ────────────────────────────
  const algoliaLibrary = suggestions.filter((s) => s.hits && s.hits.length > 0)
  const localLibrary = buildLocalLibrarySections(additiveResults)

  const merged = [...head, ...algoliaLibrary, ...localLibrary]

  let libraryHeaderShown = false
  const withHeaders = merged.map((section) => {
    if (LIBRARY_INDEXES.has(section.index)) {
      if (!libraryHeaderShown) {
        libraryHeaderShown = true
        return { ...section, showHeader: true }
      }
      return { ...section, showHeader: false }
    }
    return { ...section, showHeader: true }
  })

  // Append the global "see all results for …" footer row.
  return withHeaders.concat({ hits: [{ showMore: currentRefinement }], index: '', showHeader: false })
}

// Local-origin badge — two variants: Fuse (yellow ✓) and BM25 (blue "BM")
function LocalBadge({ hit }) {
  if (!hit.__localOrigin) return null
  if (hit.__localEngine === 'minisearch') {
    return <Icon name='local-source-bm25' className='searchResults-localBadge searchResults-localBadge--bm25' />
  }
  return <Icon name='local-source' className='searchResults-localBadge' />
}

// Render a recent-list hit. Sets get the badge icon with count; questions use
// the search-question icon. The resolved item is on suggestion.item.
function renderRecentSuggestion(suggestion) {
  const { item, __recentItemType: kind } = suggestion
  if (!item) return null
  const isSet = kind === 'set'
  const label = isSet ? item.name : getQuestionBodyLabel(item)
  return (
    <div className={`searchDropdownItem searchDropdownItem--recent searchDropdownItem--${isSet ? 'set' : 'question'}`}>
      <div className='searchDropdownItem-leadingIcon'>
        {isSet ? (
          <Icon name='search-set' questionCount={Array.isArray(item.questions) ? item.questions.length : 0} />
        ) : (
          <Icon name='search-question' />
        )}
      </div>
      <div className='searchDropdownItem-title'>
        {label}
      </div>
    </div>
  )
}

const renderSuggestion = (suggestion) => {
  let inSharedRepo = false
  let inPublicConsumerRepo = false
  let inPublicAuthorRepo = false
  if (suggestion.repo) {
    const containingRepo = getRepoforId(suggestion.repo)
    if (containingRepo) {
      inSharedRepo = containingRepo.shared
      inPublicConsumerRepo = containingRepo.role === 'consumer'
      inPublicAuthorRepo = containingRepo.role === 'author'
    }
  }
  if (suggestion.createdBy) {
    if (suggestion.published) {
      if (isCurrentUser(suggestion.author)) {
        inPublicAuthorRepo = true
      } else {
        inPublicConsumerRepo = true
      }
    }
  }

  // Quick Action — single-row create affordance
  if (suggestion.__quickAction) {
    return (
      <div className='searchDropdownItem searchDropdownItem--quickAction'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name={suggestion.icon || 'plus'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
        <LocalBadge hit={suggestion} />
      </div>
    )
  }

  // Recent item — surfaces last-touched sets/questions
  if (suggestion.__recent) {
    return renderRecentSuggestion(suggestion)
  }

  // Help "See all results for …" deep-link to help.plickers.com
  if (suggestion.__helpShowMore) {
    return (
      <div className='searchDropdownItem searchDropdownItem--showMore searchDropdownItem--helpShowMore'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='book-open' />
        </div>
        <div className='searchDropdownItem-title'>
          See all results for help
          {suggestion.query && (
            <React.Fragment>
              {' for '}
              <strong>{suggestion.query}</strong>
            </React.Fragment>
          )}
        </div>
        <div className='searchDropdownItem-trailingIcon'>
          <Icon name='open-link' />
        </div>
      </div>
    )
  }

  // Navigation entry — always use compass icon
  if (suggestion.__nav) {
    return (
      <div className='searchDropdownItem searchDropdownItem--nav'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='compass' />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
        <LocalBadge hit={suggestion} />
      </div>
    )
  }

  // External / help link — web icon leading, open-link trailing
  if (suggestion.__external) {
    return (
      <div className='searchDropdownItem searchDropdownItem--external'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='web' />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
        <LocalBadge hit={suggestion} />
        <div className='searchDropdownItem-trailingIcon'>
          <Icon name='open-link' />
        </div>
      </div>
    )
  }

  // "See all results for …" footer row
  if (suggestion.showMore) {
    return (
      <div className='searchDropdownItem searchDropdownItem--showMore'>
        <div className='searchDropdownItem-title'>
          See all results for
          {' '}
          <strong>{suggestion.showMore}</strong>
        </div>
      </div>
    )
  }

  // Pack / repo
  if (suggestion.createdBy) {
    return (
      <div className='searchDropdownItem searchDropdownItem--repo'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='search-pack' />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.name}
          {suggestion.shared && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
          {inPublicAuthorRepo && <Icon name='globe-shared-author' />}
        </div>
        <LocalBadge hit={suggestion} />
      </div>
    )
  }

  // Folder
  if (suggestion.ancestors) {
    return (
      <div className='searchDropdownItem searchDropdownItem--folder'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name={!suggestion.readOnly ? 'folder' : 'folder-isbe'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.name}
          {inSharedRepo && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
        </div>
        <LocalBadge hit={suggestion} />
      </div>
    )
  }

  // Set (has questions array) — badge shows count
  if (suggestion.questions) {
    return (
      <div className='searchDropdownItem searchDropdownItem--set'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='search-set' questionCount={Array.isArray(suggestion.questions) ? suggestion.questions.length : 0} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.name}
          {inSharedRepo && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
        </div>
        <LocalBadge hit={suggestion} />
      </div>
    )
  }

  // Question (has choices array)
  if (suggestion.choices) {
    return (
      <div className='searchDropdownItem searchDropdownItem--question'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='search-question' />
        </div>
        <div className='searchDropdownItem-title'>
          {getQuestionBodyLabel(suggestion)}
          {inSharedRepo && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
        </div>
        <LocalBadge hit={suggestion} />
      </div>
    )
  }

  return null
}

// Build autosuggest sections for local library content only (not nav/external).
// Splits local-content hits by kind so the dropdown stays organised.
function buildLocalLibrarySections(additiveResults) {
  const sections = []
  if (!Array.isArray(additiveResults) || additiveResults.length === 0) return sections

  const localBlock = additiveResults.find((r) => r.providerId === 'local-content')
  if (localBlock && Array.isArray(localBlock.hits) && localBlock.hits.length > 0) {
    const localPacks = []
    const localFolders = []
    const localItems = []
    localBlock.hits.forEach((hit) => {
      const kind = getHitKind(hit)
      if (kind === 'repo') localPacks.push(hit)
      else if (kind === 'folder') localFolders.push(hit)
      else if (kind === 'set' || kind === 'question') localItems.push(hit)
    })
    if (localPacks.length) sections.push({ index: 'Packs (Local)', hits: localPacks })
    if (localFolders.length) sections.push({ index: 'Folders (Local)', hits: localFolders })
    if (localItems.length) sections.push({ index: 'Items (Local)', hits: localItems })
  }

  return sections
}

const renderInputComponent = (inputProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <input {...inputProps} className='topNav-search' id='topNavSearchBar' />
)

function SearchAutocomplete({
  getSuggestionValue,
  inputProps,
  onSuggestionSelected,
  onSuggestionHighlighted,
  recentSuggestions,
  highlightedSuggestion,
}) {
  const { suggestions, refine, currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()

  const trimmedQuery = (currentRefinement || '').trim()
  const isEmptyQuery = trimmedQuery.length === 0

  const allSections = isEmptyQuery ?
    buildEmptyStateSections(recentSuggestions) :
    buildAllSections(suggestions, additiveResults, currentRefinement)

  // Inlined so the preview pane can close over highlightedSuggestion.
  // tabIndex={-1} keeps focus on the input while the user hovers the pane.
  const renderSuggestionsContainer = ({ containerProps, children }) => {
    if (!children) return undefined
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <div {...containerProps} className='searchDropdown searchDropdown--withPreview'>
        <div className='searchDropdown-suggestionsColumn'>
          {children}
        </div>
        {/*
          onMouseDown.preventDefault keeps the input from losing focus when
          the user clicks anything inside the preview pane (chevron buttons,
          carousel arrows). Hover stability is handled in
          SearchBar.onSuggestionHighlighted by ignoring null updates.
        */}
        <div
          className='searchDropdown-previewColumn'
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
        >
          <SearchPreviewPane suggestion={highlightedSuggestion} query={currentRefinement} />
        </div>
      </div>
    )
  }

  return (
    <Autosuggest
      suggestions={allSections}
      multiSection
      onSuggestionsFetchRequested={({ value: v }) => refine(v)}
      onSuggestionsClearRequested={() => refine('')}
      getSuggestionValue={getSuggestionValue}
      renderInputComponent={renderInputComponent}
      onSuggestionSelected={onSuggestionSelected}
      renderSuggestionsContainer={renderSuggestionsContainer}
      renderSuggestion={renderSuggestion}
      inputProps={inputProps}
      renderSectionTitle={renderSectionTitle}
      getSectionSuggestions={(section) => section.hits}
      focusInputOnSuggestionClick={false}
      onSuggestionHighlighted={onSuggestionHighlighted}
      shouldRenderSuggestions={() => true}
    />
  )
}

class SearchBar extends React.Component {
  constructor(props) {
    super(props)
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this)
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this)
    this.onSuggestionHighlighted = this.onSuggestionHighlighted.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.getSuggestionValue = this.getSuggestionValue.bind(this)
    this.onSuggestionHighlighted = debounce(this.onSuggestionHighlighted, 100)
    this.state = {
      value: '',
      highlightedSuggestion: null,
    }
  }

  onKeyDown(e) {
    if (!this.state.highlightedSuggestion) {
      if (e.keyCode === 13) {
        this.props.history.push(`/searchresults/?${qs.stringify({ query: this.state.value })}`)
        this.setState({ value: '' })
      }
    }
  }

  getSuggestionValue() {
    return this.state.value
  }

  onChange(event, { newValue }) {
    this.setState({ value: newValue })
  }

  onBlur() {
    this.setState({ value: '' })
  }

  onSuggestionsClearRequested() {
    this.setState({})
  }

  onSuggestionHighlighted({ suggestion }) {
    // Ignore the "see all results" footer row — it has no meaningful preview.
    if (suggestion && suggestion.showMore) return
    // Ignore unhighlight events (cursor moved off the suggestions list, e.g.
    // onto the preview pane). Sticking with the last real suggestion keeps
    // the preview mounted while the user interacts with it.
    if (!suggestion) return
    if (suggestion === this.state.highlightedSuggestion) return
    this.setState({ highlightedSuggestion: suggestion })
  }

  // Dispatcher for quick-action selections. Centralised so the registry's
  // `action` token is the single source of truth — add a new quick action by
  // appending to STATIC_QUICK_ACTIONS and adding one arm here.
  dispatchQuickAction(action) {
    switch (action) {
      case 'newSet':
        newSet()
        return true
      case 'newQuestion':
        newQuestion()
        return true
      case 'newRepo':
        if (this.props.showNewRepoModal) this.props.showNewRepoModal()
        return true
      case 'newSection':
        if (this.props.showNewSectionModal) this.props.showNewSectionModal()
        return true
      default:
        return false
    }
  }

  onSuggestionSelected(event, { suggestion }) {
    this.setState({ value: '' })

    if (suggestion.__quickAction) {
      this.dispatchQuickAction(suggestion.action)
      return
    }

    if (suggestion.__recent) {
      const { item, __recentItemType: kind } = suggestion
      if (item) {
        setLastLocation('search')
        this.props.history.push(kind === 'set' ? `/set/${item.id}` : `/question/${item.id}`)
      }
      return
    }

    if (suggestion.__helpShowMore) {
      window.open(buildHelpSearchUrl(suggestion.query), '_blank', 'noopener,noreferrer')
      return
    }

    if (suggestion.__external) {
      window.open(suggestion.url, '_blank', 'noopener,noreferrer')
      return
    }
    if (suggestion.__nav) {
      setLastLocation('search')
      this.props.history.push(suggestion.path)
      return
    }

    let itemDetailPath
    const itemId = suggestion.id || suggestion._id

    if (suggestion.createdBy) {
      itemDetailPath = getLinkForRepo(suggestion)
    } else if (suggestion.questions) {
      itemDetailPath = `/set/${itemId}`
    } else if (suggestion.showMore) {
      itemDetailPath = `/searchresults/?${qs.stringify({ query: suggestion.showMore })}`
    } else if (suggestion.ancestors) {
      itemDetailPath = `/library/${itemId}`
      if (suggestion.repo) {
        const parentRepo = getRepoforId(suggestion.repo)
        itemDetailPath = `${getLinkForRepo(parentRepo)}/${itemId}`
      }
    } else {
      itemDetailPath = `/question/${itemId}`
    }
    setLastLocation('search')
    this.props.history.push(`${itemDetailPath}`)
  }

  render() {
    const { value, highlightedSuggestion } = this.state
    const { recentActivities, sets, questions } = this.props
    const inputProps = {
      placeholder: 'Search',
      value,
      onChange: this.onChange,
      onBlur: this.onBlur,
      onKeyDown: this.onKeyDown,
    }

    const recentSuggestions = buildRecentSuggestions(recentActivities, sets, questions, 5)

    return (
      <SearchProviderContainer isTopNav>
        <SearchAutocomplete
          getSuggestionValue={this.getSuggestionValue}
          inputProps={inputProps}
          onSuggestionSelected={this.onSuggestionSelected}
          value={value}
          onSuggestionHighlighted={this.onSuggestionHighlighted}
          recentSuggestions={recentSuggestions}
          highlightedSuggestion={highlightedSuggestion}
        />
      </SearchProviderContainer>
    )
  }
}

function mapStateToProps(state) {
  return {
    recentActivities: state.recentActivities,
    sets: state.sets,
    questions: state.questions,
  }
}

const mapDispatchToProps = {
  showNewRepoModal,
  showNewSectionModal,
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchBar))
